/**
 * Forced-landscape viewport, driven by real pixels.
 *
 * A phone locked to portrait never reports a landscape viewport, so we always
 * PRESENT landscape: when the viewport is portrait we rotate the whole #stage 90°.
 *
 * The rotation is applied in JavaScript using the actual pixel dimensions
 * (window.innerWidth / innerHeight) rather than CSS 100vh/100vw. On iOS Safari the
 * viewport units do NOT match the real visible viewport (the URL/tool bars are
 * counted differently), which offsets the rotated content and leaves letterbox
 * gaps. Reading innerWidth/innerHeight — and re-reading them on resize /
 * orientationchange / visualViewport resize — keeps the stage locked to the true
 * visible area.
 *
 * Only the status bar (env(safe-area-inset-top)) is inset; the content runs to the
 * bottom and both sides with no black bars. Touch coordinates are mapped back into
 * stage space with the SAME pixel values so the picture and the input never drift.
 *
 * Geometry (rotated 90° clockwise). W = innerWidth, H = innerHeight, T = status-bar
 * inset. The landscape stage occupies the screen region x∈[0,W], y∈[T,H]:
 *   local origin at screen (W, T); size (H - T) × W; transform-origin 0 0; rotate 90°.
 *   forward  (stage → screen): sx = W - ly,      sy = T + lx
 *   inverse  (screen → stage): lx = sy - T,      ly = W - sx
 */
import { IS_TOUCH } from './device';

let stageEl: HTMLElement | null = null;
let probe: HTMLDivElement | null = null;
let inited = false;

/* Cached, updated by syncViewport() — the single source the coordinate map reads. */
let vpRotated = false;
let vpW = typeof window !== 'undefined' ? window.innerWidth : 0;
let vpSafeTop = 0;

/** Resolve env(safe-area-inset-top) to pixels via a hidden probe element. */
function safeInsetTop(): number {
  if (!probe) {
    probe = document.createElement('div');
    probe.id = 'echo-safe-probe';
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;visibility:hidden;pointer-events:none;' +
      'height:env(safe-area-inset-top, 0px);';
    document.body.appendChild(probe);
  }
  return probe.offsetHeight || 0;
}

/** The rotation stage that wraps the canvas and all overlays. */
export function getStage(): HTMLElement {
  if (!stageEl) {
    stageEl = document.getElementById('stage');
    if (!stageEl) {
      stageEl = document.createElement('div');
      stageEl.id = 'stage';
      document.body.appendChild(stageEl);
    }
    if (IS_TOUCH) stageEl.classList.add('touch');
  }
  if (!inited) {
    inited = true;
    syncViewport();
    // Registered here — before the Engine constructs — so on shared events this
    // runs first and the engine reads an already-updated stage size.
    window.addEventListener('resize', syncViewport);
    window.addEventListener('orientationchange', syncViewport);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', syncViewport);
    }
  }
  return stageEl;
}

/** Recompute the stage layout from live pixel dimensions. Cheap; safe to spam. */
export function syncViewport(): void {
  const stage = getStage();
  const W = window.innerWidth;
  const H = window.innerHeight;
  // A zero-sized viewport (a hidden/detached tab reports 0) would collapse the
  // stage to nothing; keep the last good layout until real dimensions arrive.
  if (W === 0 || H === 0) return;
  vpW = W;

  if (!IS_TOUCH) {
    vpRotated = false;
    vpSafeTop = 0;
    return; // desktop keeps the CSS full-viewport layout
  }

  const top = safeInsetTop();
  vpSafeTop = top;
  vpRotated = W < H;

  if (vpRotated) {
    // Landscape content in the screen region x∈[0,W], y∈[top,H].
    stage.style.left = `${W}px`;
    stage.style.top = `${top}px`;
    stage.style.width = `${H - top}px`;
    stage.style.height = `${W}px`;
    stage.style.transformOrigin = '0 0';
    stage.style.transform = 'rotate(90deg)';
  } else {
    // Already landscape: just drop below the status bar, fill the rest.
    stage.style.left = '0px';
    stage.style.top = `${top}px`;
    stage.style.width = `${W}px`;
    stage.style.height = `${H - top}px`;
    stage.style.transformOrigin = '0 0';
    stage.style.transform = 'none';
  }
}

/** True when the stage is currently rotated (touch device + portrait viewport). */
export function isRotated(): boolean {
  return vpRotated;
}

/** Width of the presented (landscape) stage in px. */
export function stageWidth(): number {
  if (!IS_TOUCH) return window.innerWidth;
  return vpRotated ? window.innerHeight - vpSafeTop : window.innerWidth;
}

/** Map a raw touch/pointer screen coordinate into stage (landscape) space, using
 *  the same pixel values that positioned the stage so picture and input align. */
export function mapClientToStage(clientX: number, clientY: number): { x: number; y: number } {
  if (!IS_TOUCH) return { x: clientX, y: clientY };
  if (vpRotated) {
    return { x: clientY - vpSafeTop, y: vpW - clientX };
  }
  return { x: clientX, y: clientY - vpSafeTop };
}
